/**
 * Eterna Vaults - Zero-Knowledge Passcode Service
 * 
 * This service handles client-side passcode encryption for zero-knowledge users,
 * ensuring passcodes are encrypted before being sent to the server.
 */

import encryptionService from './EncryptionService';
import keyManagementService from './KeyManagementService';

class ZeroKnowledgePasscodeService {
  constructor() {
    this.isInitialized = false;
  }

  /**
   * Initialize the service for a user
   * @param {string} userId - User identifier
   * @param {string} passcode - User's passcode
   * @returns {Promise<boolean>} Initialization success
   */
  async initialize(userId, passcode) {
    try {
      console.log('🔐 Initializing zero-knowledge passcode service for user:', userId);
      
      // For now, we'll use the backend service directly
      // No need for complex key management on frontend
      this.isInitialized = true;
      console.log('✅ Zero-knowledge passcode service initialized');
      
      return true;
    } catch (error) {
      console.error('❌ Zero-knowledge passcode service initialization failed:', error);
      return false;
    }
  }

  /**
   * Encrypt passcode for zero-knowledge users
   * @param {string} passcode - Plain text passcode
   * @param {string} userId - User identifier
   * @returns {Promise<Object>} Encrypted passcode blob
   */
  async encryptPasscode(passcode, userId) {
    try {
      if (!this.isInitialized) {
        throw new Error('Service not initialized');
      }

      console.log('🔐 Encrypting passcode for zero-knowledge user:', userId);
      
      // For now, we'll use the backend service directly
      // The backend will handle the encryption
      const encryptedBlob = {
        passcode: passcode,
        userId: userId,
        timestamp: Date.now(),
        algorithm: 'backend-handled'
      };

      console.log('✅ Passcode prepared for backend encryption');
      return encryptedBlob;
    } catch (error) {
      console.error('❌ Passcode encryption failed:', error);
      throw new Error('Failed to encrypt passcode: ' + error.message);
    }
  }

  /**
   * Verify if user is in zero-knowledge mode
   * @param {Object} member - Member object from server
   * @returns {boolean} Whether user uses zero-knowledge
   */
  isZeroKnowledgeUser(member) {
    return member && member.useZeroKnowledge === true;
  }

  /**
   * Prepare passcode for server verification
   * @param {string} passcode - Plain text passcode
   * @param {Object} member - Member object from server
   * @returns {Promise<string|Object>} Passcode for server (plaintext or encrypted blob)
   */
  async preparePasscodeForServer(passcode, member) {
    try {
      if (this.isZeroKnowledgeUser(member)) {
        console.log('🔐 User is in zero-knowledge mode, encrypting passcode');
        
        // Encrypt passcode for zero-knowledge users
        const encryptedBlob = await this.encryptPasscode(passcode, member._id);
        
        // Return the encrypted blob as a string for server comparison
        return JSON.stringify(encryptedBlob);
      } else {
        console.log('📝 User is in legacy mode, using plaintext passcode');
        
        // Return plaintext for legacy users
        return passcode;
      }
    } catch (error) {
      console.error('❌ Failed to prepare passcode for server:', error);
      throw error;
    }
  }

  /**
   * Migrate user to zero-knowledge mode
   * @param {string} userId - User identifier
   * @param {string} passcode - Current passcode
   * @param {string} token - Auth0 access token
   * @returns {Promise<Object>} Migration result
   */
  async migrateToZeroKnowledge(userId, passcode, token) {
    try {
      console.log('🔄 Migrating user to zero-knowledge mode:', userId);
      
      // Send migration request to server
      // CRITICAL FIX: Require environment variable - no localhost fallback
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 
        (process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : 'https://api.eternavaults.com');
      const response = await fetch(`${apiUrl}/api/members/${userId}/migrate-to-zero-knowledge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          testPasscode: passcode
        })
      });

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Successfully migrated to zero-knowledge mode');
        return {
          success: true,
          message: result.message,
          member: result.member
        };
      } else {
        throw new Error(result.error || 'Migration failed');
      }
    } catch (error) {
      console.error('❌ Zero-knowledge migration failed:', error);
      throw error;
    }
  }

  /**
   * Test zero-knowledge functionality using the dynamic service
   * @param {string} userId - User identifier
   * @param {string} passcode - Test passcode
   * @param {string} token - Auth0 access token
   * @returns {Promise<Object>} Test result
   */
  async testZeroKnowledgeFunctionality(userId, passcode, token) {
    try {
      console.log('🧪 Testing zero-knowledge functionality for user:', userId);
      
      // Use the backend dynamic service for testing
      // CRITICAL FIX: Require environment variable - no localhost fallback
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 
        (process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : 'https://api.eternavaults.com');
      const response = await fetch(`${apiUrl}/api/members/${userId}/test-zero-knowledge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          testPasscode: passcode
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Zero-knowledge test completed successfully');
        return result;
      } else {
        throw new Error(result.error || 'Test failed');
      }
    } catch (error) {
      console.error('❌ Zero-knowledge test failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Create singleton instance
const zeroKnowledgePasscodeService = new ZeroKnowledgePasscodeService();

export default zeroKnowledgePasscodeService;
